import { HttpClient } from '@angular/common/http';
import { CacheService } from '../cache';
import { ResourceService } from './resource';
import { User, IUser } from '../../models/user';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { EventService } from './../events';
import { DOMAIN, EVENTS } from '../../constants';
import { ACCOUNT_STATUS } from '../../models/user';

@Injectable({
  providedIn: 'root'
})
export class UserService extends ResourceService<User> {


  /**
   * @constructor
   * Creates an instance of the UserService
   *
   * @param {HttpClient} http Injected instance of HttpClient
   * @param {CacheService} cache Injected instance of CacheService
 */

  constructor(
    protected http: HttpClient,
    protected cache: CacheService,
    private events: EventService,

  ) {
    super(http, cache, 'users');
  }


  exists(email: string): Observable<any> {
    return this.http.get(`${this.endpoint}/exists`, { params: { q: email } });
  }

  /**
    * Sends a POST request to the /users/:id/debit-card endpoint
    * @remarks
    * to add a users debit card
    * @param {string} id: unique identifier of the user
    * @param {any} data: Body of request sent to the server
    * @returns {Observable} Returns an observable of object
  */
  add_debit_card(id: string, data: any) {
    return this.http.post(`${this.endpoint}/${id}/debit-cards`, data);
  }

  /**
    * Sends a DELETE request to the /users/:id/debit-cards/${payment_method} endpoint
    * @remarks
    * to remove a users debit card details from the server
    * @param {string} id: unique identifier of the user
    * @param {any} payment_method: used to determing the selected payment method
    * @returns {Observable} Returns an observable of object
  */
  delete_debit_card(id: string, payment_method: any) {
    return this.http.delete(`${this.endpoint}/${id}/debit-cards/${payment_method}`);
  }

  /**
    * Sends a PUT request to the /users/:id/debit-cards/${payment_method} endpoint
    * @remarks
    * to update a users debit card
    * @param {string} id: unique identifier of the user
    * @param {any} payment_method: used to determing the selected payment method
    * @param {any} payload: Body of request sent to the server
    * @returns {Observable} Returns an observable of object
  */

  update_debit_card(id: string, payment_method: any, payload: any) {
    return this.http.put(`${this.endpoint}/${id}/debit-cards/${payment_method}`, payload);
  }

  /**
    * Sends a GET request to the /users/:id/referrals endpoint
    * @remarks
    * to get the lists of referrals from a single user
    * @param {string} id: unique identifier of the user
    * @returns {Observable} Returns an observable of object
  */
  fetch_referrals(id: string) {
    return this.http.get<any>(`${this.endpoint}/${id}/referrals`)
      .pipe(map((resp: any) => resp.data.users))
  }

  /**
    * Sends a PUT request to the /users/:id/password endpoint of the typed Resource
    * @remarks
    * to change password of a user
    * @param {string} id: unique identifier of the user
    * @param {any} data: body of request made to the endpoint
    * @returns {Observable} Returns an observable of object
  */
  change_password(id: string, data: any) {
    return this.http.put<User>(`${this.endpoint}/${id}/password`, data)
  }

  /**
    * Sends a PUT request to the /users/:id/trusted endpoint of the typed Resource
    * @remarks
    * to toggle a user providing property(s) on Clooper trusted or untrusted
    * @param {string} id: unique identifier of the user
    * @param {any} data: body of request made to the endpoint
    * @returns {Observable} Returns an observable of object
  */
  toggle_trusted_partner(data: { id: string, trusted: boolean }) {
    return this.http.put<User>(`${this.endpoint}/${data.id}/trusted`, data).pipe(map(resp => {
      this.cache.remove(`${this.endpoint}/${data.id}`);
      this.cache.remove(`${this.endpoint}`);
      return resp.data.user;
    }))
  }

  /**
    * Sends a PUT request to the /users/:id/blocked endpoint of the typed Resource
    * @remarks
    * to toggle a user providing property(s) on Clooper trusted or untrusted
    * @param {string} id: unique identifier of the user
    * @param {any} data: body of request made to the endpoint
    * @returns {Observable} Returns an observable of object
  */
   toggle_block_user(item: { id: string, blocked: boolean }) {
    return this.http.put<User>(`${this.endpoint}/${item.id}/blocked`, item).pipe(map(resp => {
      this.cache.remove(`${this.endpoint}/${item.id}`);
      this.cache.remove(`${this.endpoint}`);
      return resp.data.user;
    }))
  }

  /**
    * Sends a PUT request to the /users/trusted endpoint of the typed Resource
    * @remarks
    * to toggle a user providing property(s) on Clooper trusted or untrusted
    * @param {string} ids: unique identifier of the user
    * @param {any} data: body of request made to the endpoint
    * @returns {Observable} Returns an observable of object
  */
   bulk_toggle_trusted_partners(data: { id?: string, ids?: string[], trusted: boolean }): Observable<any> {
    return this.http.put(`${this.endpoint}/trusted`, data).pipe(map(resp => {
      data.ids?.forEach(id => this.cache.remove(`${this.endpoint}/${id}`));
      this.cache.remove(`${this.endpoint}`);
      return resp;
    }))
  }

  /**
    * Sends a PUT request to the /users/blocked endpoint of the typed Resource
    * @remarks
    * to toggle a user providing property(s) on Clooper trusted or untrusted
    * @param {string} ids: unique identifier of the user
    * @param {any} data: body of request made to the endpoint
    * @returns {Observable} Returns an observable of object
  */
   bulk_toggle_block_users(data: { id?: string, ids?: string[], blocked: boolean }): Observable<any> {
    return this.http.put(`${this.endpoint}/blocked`, data).pipe(map(resp => {
      data.ids?.forEach(id => this.cache.remove(`${this.endpoint}/${id}`));
      this.cache.remove(`${this.endpoint}`);
      return resp;
    }))
  }


  /**
    * Sends a PUT request to the /users/:id/trusted endpoint of the typed Resource
    * @remarks
    * to toggle a user providing property(s) on Clooper trusted or untrusted
    * @param {string} id: unique identifier of the user
    * @param {any} data: body of request made to the endpoint
    * @returns {Observable} Returns an observable of object
  */
  updateBusinessUserProfile(id: string, data: any) {
    return this.http.put<User>(`${this.endpoint}/temp/${id}`, data).pipe(map(resp => {
      this.cache.remove(`${this.endpoint}/${id}`);
      this.cache.remove(`${this.endpoint}`);
      this.events.publish(EVENTS.UPDATE_USER, resp.data.user);
      const user = resp.data.user;
        // this.currentUser = user;
        // this.admin = res.data.admin;
        return user;
    }))
  }

  // ── NEW METHOD ────────────────────────────────────────


updateAccountStatus(id: string, status: ACCOUNT_STATUS): Observable<User> {
  return this.http.put<User>(`${this.endpoint}/${id}/account-status`, {
    account_status: status
  }).pipe(
    map((response: any) => {
      const updatedUser = response?.data?.user || response;

      this.cache.remove(`${this.endpoint}/${id}`);
      this.cache.remove(`${this.endpoint}`);

      return updatedUser;
    })
  );
}
}
